import React from 'react';
import { Link } from 'react-router-dom';
import { usePageMeta } from '../lib/usePageMeta';

const bodyTextClassName = 'text-body';
const sectionTitleClassName = 'text-2xl md:text-3xl font-medium tracking-tight pt-2';
const articleTitle = 'Introducing MIRA';
const articleDescription = 'Memory Inference and Retrieval Architecture, a memory system for AI.';
const articleOgImage = '/og/introducing-mira.png';

const scoreBreakdown = [
    { label: 'Overall', value: '90.6%', detail: '1,396 / 1,540' },
    { label: 'Open-domain', value: '94.2%', detail: 'broad conversation recall' },
    { label: 'Single-hop', value: '87.6%', detail: 'direct memory retrieval' },
    { label: 'Temporal', value: '87.5%', detail: 'dated memory recall' },
    { label: 'Multi-hop', value: '79.2%', detail: 'cross-memory synthesis' },
];

const reportedScores = [
    { system: 'EverMemOS', score: '93.05%', source: 'EverMind' },
    { system: 'ByteRover', score: '92.2%', source: 'ByteRover' },
    { system: 'MemMachine', score: '91.69%', source: 'MemMachine' },
    { system: 'Mem0', score: '91.6%', source: 'Mem0' },
    { system: 'MIRA', score: '90.6%', source: 'SilentLabs curated run evidence' },
    { system: 'Backboard', score: '90.0%', source: 'Backboard GitHub' },
    { system: 'Honcho', score: '89.9%', source: 'Plastic Labs' },
];

const chartMin = 80;
const chartMax = 100;
const chartRange = chartMax - chartMin;

const memorySurfaces = [
    ['Messages', 'The conversation record, preserved as source evidence across user and assistant turns.'],
    ['Facts', 'Dated statements, preferences, corrections, and temporal claims extracted from the record.'],
    ['Core memory', 'Stable context kept close to the response model.'],
    ['Entities', 'People, projects, concepts, preferences, and events that help retrieval stay on the thread.'],
    ['Documents', 'Optional long-form context with a separate embedding path.'],
];

const MiraMemoryInferenceRetrievalArchitecture: React.FC = () => {
    usePageMeta({
        title: `${articleTitle} | SilentLabs Research`,
        description: articleDescription,
        image: articleOgImage,
        type: 'article',
    });

    return (
        <main id="main-content" className="w-full bg-canvas" role="main">
            <section className="pt-32 pb-12 px-6 md:px-16 lg:px-24 text-center">
                <div className="max-w-4xl mx-auto">
                    <p className="text-base md:text-lg text-white mb-6">
                        April 24, 2026
                    </p>
                    <h1>Introducing MIRA</h1>
                    <p className="w-full mt-8 text-body text-white text-balance">
                        Memory Inference and Retrieval Architecture, a memory system for AI.
                    </p>
                    <hr
                        aria-hidden="true"
                        className="mt-[4.5rem] mx-auto w-full max-w-4xl border-0"
                        style={{ borderTop: '2px solid rgba(255, 255, 255, 0.28)' }}
                    />
                </div>
            </section>

            <section className="pt-12 md:pt-16 pb-[191px] px-6 md:px-12 lg:px-24">
                <article className="max-w-4xl mx-auto px-6 space-y-12 text-left">
                    <div className="space-y-5">
                        <p className={bodyTextClassName}>
                            Today we’re releasing MIRA v0.1.0, an open-source implementation of the memory architecture behind our long-term AI memory work.
                        </p>
                        <p className={bodyTextClassName}>
                            Long-running AI systems need memory that can hold onto exact language, preserve context across sessions, and use that context when it is relevant, or when they are asked to do so.
                        </p>
                        <p className={bodyTextClassName}>
                            MIRA is our first open release of that work. It brings together raw conversation storage, dated fact extraction, handling of corrections made to facts or assumptions over time, and understanding of the exact topic being discussed or asked about.
                        </p>
                        <p className={bodyTextClassName}>
                            We benchmarked MIRA on LoCoMo, a long-term conversational memory benchmark built around very long, multi-session conversations. The dataset contains 10 conversations, 272 sessions, and 5,882 turns. Systems are evaluated on the accuracy of answers grounded in that conversation set, across temporal, open-domain, and single-hop question categories, as well as multi-hop questions that require connecting multiple pieces of context. MIRA v0.1.0 reports 90.6% on LoCoMo QA categories 1-4, with 1,396 correct answers out of 1,540, placing it among the strongest publicly reported LoCoMo QA category-1-through-4 memory-system results.
                        </p>
                        <div className="my-5 overflow-hidden bg-white/[0.025] py-6 md:py-8">
                            <div className="w-full">
                                <div>
                                    <h2 className="text-2xl md:text-3xl font-medium tracking-tight text-white">Public LoCoMo reports</h2>
                                    <p className="mt-3 text-base text-text-secondary">One row per organization; public protocols vary by category scope, model/judge, inputs, retry policy, and available logs.</p>
                                </div>
                                <div className="mt-10 grid grid-cols-[2.25rem_1fr] gap-3">
                                    <div className="relative h-[340px] text-base text-text-secondary">
                                        {[100, 95, 90, 85, 80].map((tick) => (
                                            <span
                                                key={tick}
                                                className="absolute right-0 -translate-y-1/2"
                                                style={{ top: `${((chartMax - tick) / chartRange) * 100}%` }}
                                            >
                                                {tick}
                                            </span>
                                        ))}
                                    </div>
                                    <div className="relative h-[340px] border-b border-white/20">
                                        {[100, 95, 90, 85].map((tick) => (
                                            <div
                                                key={tick}
                                                className="absolute left-0 right-0 border-t border-dashed border-white/[0.07]"
                                                style={{ top: `${((chartMax - tick) / chartRange) * 100}%` }}
                                            />
                                        ))}
                                        <div className="absolute inset-x-0 bottom-0 flex h-full items-end justify-between gap-3 px-2 md:gap-5 md:px-3">
                                            {reportedScores.map((row) => {
                                                const value = parseFloat(row.score);
                                                const height = Math.max(0, Math.min(100, ((value - chartMin) / chartRange) * 100));
                                                const isMira = row.system.startsWith('MIRA');

                                                return (
                                                    <div key={row.system} className="flex h-full flex-1 flex-col items-center justify-end">
                                                        <p className="mb-3 text-base text-white">{row.score}</p>
                                                        <div
                                                            className={`w-full max-w-[56px] rounded-t-lg md:max-w-[68px] ${isMira ? 'bg-gradient-to-t from-[#7d2ee8] to-[#b99cff]' : 'bg-[#9ab8ea]'}`}
                                                            style={{ height: `${height}%` }}
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    <div aria-hidden="true" />
                                    <div
                                        className="grid gap-3 px-2 pt-5 text-center text-base text-text-secondary md:gap-5 md:px-3"
                                        style={{ gridTemplateColumns: `repeat(${reportedScores.length}, minmax(0, 1fr))` }}
                                    >
                                        {reportedScores.map((row) => (
                                            <div key={row.system} className="min-w-0">
                                                <p className="text-white">{row.system}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <section className="space-y-8">
                        <h2 className={sectionTitleClassName}>Built for precise recall</h2>
                        <p className={bodyTextClassName}>
                            Conversation and collaboration with AI systems naturally create a demand for continuous persistent memory. Users expect the interaction to feel intuitive, with context carrying forward instead of being lost.
                        </p>
                        <p className={bodyTextClassName}>
                            By keeping every turn in a multi-level memory system, with raw messages, dated facts, correction events, topic anchors, and core memory close to the response model, MIRA is built for those moments.
                        </p>
                        <p className={bodyTextClassName}>
                            Long-term memory often fails at the boundary between context and evidence, losing the exact phrase, dropping the date, surfacing an older memory without the correction that replaced it, collapsing counts into guesses, blurring names and relationships, and breaking continuity when the conversation needs it most.
                        </p>
                        <p className={bodyTextClassName}>
                            During our development of MIRA, we treated those failures as architectural problems. The result is memory that is more reliable and more usable across time, letting a user return days or months later and continue the conversation while significantly reducing the need to reintroduce details that were already established.
                        </p>
                    </section>

                    <section className="space-y-8">
                        <h2 className={sectionTitleClassName}>Architecture</h2>
                        <p className={bodyTextClassName}>
                            MIRA separates memory into surfaces with distinct responsibilities, which together form a system that preserves evidence, compacts stable context, and retrieves the right form of memory depending on the type and nature of the conversation. Raw messages remain the source of truth and evidence. Facts preserve durable statements with resolved dates and topics. Core memory keeps stable context close to the model. And entities provide stable retrieval anchors: people, projects, preferences, events, and topics to lay a strong foundation for retrieval.
                        </p>
                        <div className="my-3 overflow-x-auto rounded-2xl">
                            <table className="w-full min-w-[620px] border-collapse text-left">
                                <thead className="border-b border-white/24 text-text-primary">
                                    <tr>
                                        <th className="w-48 px-5 py-4 text-white font-semibold">Surface</th>
                                        <th className="px-5 py-4 text-white font-semibold">Role</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-white/10 text-text-primary">
                                    {memorySurfaces.map(([surface, role]) => (
                                        <tr key={surface}>
                                            <td className="w-48 px-5 py-4 text-white font-medium">{surface}</td>
                                            <td className="px-5 py-4 text-white">{role}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <p className={bodyTextClassName}>
                            Each turn moves through the same memory cycle: preserve the source conversation, update the durable memory surfaces, and prepare the context the model needs for the next response.
                        </p>
                    </section>

                    <section className="space-y-8">
                        <h2 className={sectionTitleClassName}>Retrieval</h2>
                        <p className={bodyTextClassName}>
                            One of the strongest parts of the underlying architecture is how it handles the current turn before the next reply. MIRA loads core memory, classifies the user's input, and decides how much context the response needs based on the explicit request and the implicit memory work required by the conversation.
                        </p>
                        <p className={bodyTextClassName}>
                            This decision is made through six retrieval routes: surface, deep, global deep, counting, frequency, and conceptual. A relational layer can also be added when the query needs cross-context inference. Depending on the route, the system may only use the basic core memory context, as it does for surface queries, or choose between raw messages, entities, facts, documents, or a combination of them. The deeper routes combine vector retrieval, full-text search for questions where precision matters most, keyword matching, and targeted entity search that together create an advanced, unified hybrid approach that ensures no relevant context is missed.
                        </p>
                    </section>

                    <section className="space-y-8">
                        <h2 className={sectionTitleClassName}>Memory inference</h2>
                        <p className={bodyTextClassName}>
                            While retrieval brings evidence into the response, memory inference decides how that evidence should be used. This layer gives MIRA a way to handle context that belongs to a specific date, distinguish original facts from later corrections, and treat exact-message recall as the source of truth when multiple memory layers are involved.
                        </p>
                        <p className={bodyTextClassName}>
                            The policy is deliberately practical. Rather than leaving memory use entirely to the response model, MIRA teaches the model how to use the system: when to trust resolved dates, when to prioritize corrections, when to use raw messages, and when to gather distinct supported items before answering.
                        </p>
                    </section>

                    <section className="space-y-8">
                        <h2 className={sectionTitleClassName}>Evaluations</h2>
                        <p className={bodyTextClassName}>
                            In our LoCoMo evaluation, we used gpt-5-mini for answer generation and judging. This score covers LoCoMo QA categories 1-4. Category 5/adversarial is excluded, and public protocols vary across systems.
                        </p>
                        <p className={bodyTextClassName}>
                            Evaluation scope: MIRA v0.1.0 reports 90.6% on LoCoMo QA categories 1-4 (1,396 / 1,540), using gpt-5-mini for answer generation and judging. The scored run was runtime-clean: ground-truth answers, gold evidence annotations, category labels, judge outputs, curated evidence, failed-question analyses, compressed logs, prior predictions, and previous failed-run state were not available to MIRA during ingestion, retrieval, prompt construction, or answer generation.
                        </p>
                        <p className={bodyTextClassName}>
                            Evidence with per-category breakdowns and failed questions is included in the GitHub repository for audit and post-run analysis, not as runtime memory or answer-time retrieval material.
                        </p>
                        <div className="my-3 overflow-x-auto rounded-2xl">
                            <table className="w-full min-w-[620px] border-collapse text-left">
                                <thead className="border-b border-white/24 text-text-primary">
                                    <tr>
                                        <th className="w-48 px-5 py-4 text-white font-semibold">Category</th>
                                        <th className="px-5 py-4 text-white font-semibold">Score</th>
                                        <th className="px-5 py-4 text-white font-semibold">Detail</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-white/10 text-text-primary">
                                    {scoreBreakdown.map((metric) => (
                                        <tr key={metric.label}>
                                            <td className="w-48 px-5 py-4 text-white font-medium">{metric.label}</td>
                                            <td className="px-5 py-4 text-white">{metric.value}</td>
                                            <td className="px-5 py-4 text-white">{metric.detail}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </section>

                    <section className="space-y-8">
                        <h2 className={sectionTitleClassName}>Availability</h2>
                        <p className={bodyTextClassName}>
                            MIRA v0.1.0 is open source and available as a developer preview on the SilentLabs GitHub organization.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4">
                            <a
                                href="https://github.com/SilentLabsAI/mira"
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center justify-center rounded-full bg-white px-6 py-3 text-sm font-medium text-black transition-opacity hover:opacity-85"
                            >
                                View GitHub repository
                            </a>
                            <Link
                                to="/research"
                                className="inline-flex items-center justify-center rounded-full border border-white/20 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-white/10"
                            >
                                Back to Research
                            </Link>
                        </div>
                    </section>

                </article>
            </section>
        </main>
    );
};

export default MiraMemoryInferenceRetrievalArchitecture;
